import { useState, useEffect, useRef, useCallback } from 'react';
import { generateActivityReportInsight, InsightResponse } from 'api/activityReport';

// Session cache - in-memory Map that persists for the session
const sessionCache = new Map<string, InsightResponse>();

export interface InsightError {
  code: string;
  message: string;
}

export interface UseActivityReportInsightParams {
  customerId: number | null;
  addressId: number | null;
  dateFrom: string | null;
  dateTo: string | null;
  positiveOnly?: boolean;
}

/**
 * Generate cache key from params
 */
function generateCacheKey(params: UseActivityReportInsightParams): string {
  return `activity-report:${params.customerId}:${params.addressId}:${params.dateFrom}:${params.dateTo}:${params.positiveOnly || false}`;
}

/**
 * Hook for fetching AI insights for activity report
 * @param params - Filter parameters
 * @returns Object containing insight data, loading state, error, and refetch function
 */
export function useActivityReportInsight(params: UseActivityReportInsightParams) {
  const [insight, setInsight] = useState<InsightResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<InsightError | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchInsight = useCallback(async (skipCache = false) => {
    const { customerId, addressId, dateFrom, dateTo, positiveOnly } = params;

    // Don't fetch if required params are missing
    if (!customerId || !addressId || !dateFrom || !dateTo) {
      setInsight(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const cacheKey = generateCacheKey(params);

    // Check cache first (unless skipCache is true)
    if (!skipCache && sessionCache.has(cacheKey)) {
      const cachedData = sessionCache.get(cacheKey)!;
      setInsight(cachedData);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Show loading state
    setIsLoading(true);
    setError(null);

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const data = await generateActivityReportInsight({
        customerId,
        addressId,
        dateFrom,
        dateTo,
        positiveOnly,
      });

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      // Store in cache
      sessionCache.set(cacheKey, data);

      setInsight(data);
      setError(null);
    } catch (err: any) {
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      console.error('Error fetching activity report insight:', err);

      // Handle error with code
      if (err.code === 'INSUFFICIENT_DATA') {
        setError({ code: 'INSUFFICIENT_DATA', message: err.message || 'Not enough data for insights' });
      } else {
        setError({ code: err.code || 'GENERIC', message: err.message || 'Could not generate insight' });
      }

      setInsight(null);
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [params.customerId, params.addressId, params.dateFrom, params.dateTo, params.positiveOnly]);

  // Debounced effect for param changes
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't set up timer if params are invalid
    if (!params.customerId || !params.addressId || !params.dateFrom || !params.dateTo) {
      setInsight(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Set up debounce timer (1 second)
    debounceTimerRef.current = setTimeout(() => {
      fetchInsight();
    }, 1000);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchInsight]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    fetchInsight(true); // Skip cache on manual refetch
  }, [fetchInsight]);

  return {
    insight,
    isLoading,
    error,
    refetch,
  };
}

export default useActivityReportInsight;
